import React, {
    useEffect,
    useRef,
    useState,
    Dispatch,
    SetStateAction,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
    setActiveLink,
    setActiveIcon,
} from '../../features/sidebar/sidebarSlice';
import UserPreferenceEditor from '../common/UserPreferenceEditor';
import {
    DashBoardIcon,
    DashBoardIconBlue,
    LogOut,
    NucleusIconBlue,
    OpenBookIcon,
    OpenBookIconBlue,
    ScholarIconBlue,
    SchoolIcon,
    SchoolIconBlue,
    UserAvatar,
    ASMBlueIcon,
    LeadLogo,
    SmartTeachLogo,
    StudentListIcon,
    ReportsIcon,
    JasperIcon,
    BuildrSideNavLogo,
    WidgetLogo,
    WidgetLogoAllDone,
    HelpAndFAQIcon,
    HelpAndFAQBlueIcon,
    SettingsIcon,
    SettingsBlueIcon,
    StudentReportIconBlue,
    StudentReportIcon,
} from '../../assets';
import DefaultIcon from '../../assets/images/default-icon.svg';
import { trackEvent } from '../../segment';
import {
    setOfflineModalOpen,
    setSideNavIconClickedName,
} from '../../features/offlineModal/offlineModalSlice';
import { useOpenBrowserMutation } from '../../api/open-browser';
import { setIframeDisplayUrl } from '../../features/iframeDisplay/iframeDisplaySlice';
import { IPendingCardSection } from '../../api/pending-card';
import { setPreferenceOpen } from '../../features/Preferences/PreferencesSlice';
import { setJasperIframeOpen } from '../../features/jasperIframe/jasperIframeSlice';
import { Tooltip } from 'antd';

interface ModalProps {
    schoolId: string | number;
    dealId: string | number;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    isPendingActionsModalOpen: boolean;
    setIsPendingActionsModalOpen: (value: boolean | null) => void;
    pendingActionsCount: number;
    collapsed: boolean;
    setCollapsed: Dispatch<SetStateAction<boolean>>;
    isGetStatusLoading: boolean;
    widgetStatus?: boolean;
    getStatusData:
        | {
              data: {
                  sections: {
                      [key: string]: IPendingCardSection;
                  };
                  totalSections: number;
                  sectionsCompleted: number;
              } | null;
          }
        | undefined;
}

const icon_mapping: { [key in string]: string } = {
    ActiveBookLibraryIcon: OpenBookIconBlue,
    InactiveBookLibraryIcon: OpenBookIcon,
    ActiveStudentListIcon: ScholarIconBlue,
    InactiveStudentListIcon: StudentListIcon,
    ActiveReportsIcon: ASMBlueIcon,
    InactiveReportsIcon: ReportsIcon,
    ActiveJasperIcon: NucleusIconBlue,
    InactiveJasperIcon: JasperIcon,
    ActiveLeadGroupAcademyIcon: SchoolIconBlue,
    InactiveLeadGroupAcademyIcon: SchoolIcon,
    ActiveNucleusIcon: DashBoardIconBlue,
    InactiveNucleusIcon: DashBoardIcon,
    ActiveHelpAndFAQIcon: HelpAndFAQBlueIcon,
    InActiveHelpAndFAQIcon: HelpAndFAQIcon,
    ActiveSettingsIcon: SettingsIcon,
    InActiveSettingsIcon: SettingsIcon,
    ActiveStudenAppIcon: StudentReportIconBlue,
    InactiveStudentAppIcon: StudentReportIcon,
};
const SideNavigationBar: React.FC<ModalProps> = ({
    isPendingActionsModalOpen,
    setIsModalOpen,
    setIsPendingActionsModalOpen,
    pendingActionsCount,
    setCollapsed,
    collapsed,
    isGetStatusLoading,
    getStatusData,
    widgetStatus,
    schoolId,
    dealId,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const activeIcon = useSelector(
        (state: RootState) => state.sidebar.activeIcon
    );
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const user = useSelector((state: RootState) => state.auth.user);
    const schoolInfo = useSelector((state: RootState) => state.SchoolInfo.data);
    const product = useSelector((state: RootState) => state.product);
    const productType = useSelector((state: RootState) => {
        return product === 'Buildr' ? 'Buildr' : state.SchoolInfo.data.product;
    });
    const navList = useSelector((state: RootState) => state.SideBarConfig.data);
    const [logo, setLogo] = useState('');
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const isPreferenceOpen = useSelector(
        (state: RootState) => state.Preference.isOpen
    );
    const [isWidgetLogoClicked, setIsWidgetLogoClicked] = useState(false);
    const isFullScreenModalOpen = useSelector(
        (state: RootState) => state.FullScreenModal.isOpen
    );
    const classDivision = useSelector(
        (state: RootState) => state.classSubjects
    );
    const installationPackage = useSelector(
        (state: RootState) =>
            state.SchoolInfo.data.installation_package?.INSTALLATION_MODE
    );
    const isTransparentModalOpen = useSelector(
        (state: RootState) => state.TransparentModal.isOpen
    );
    const [
        getOpenBrowser,
        { isLoading: isOpenBrowserLoading, isError: isOpenBrowserError },
    ] = useOpenBrowserMutation();

    // Event listener for online status change
    const handleOnline = () => {
        setIsOnline(true);
    };

    // Event listener for offline status change
    const handleOffline = () => {
        setIsOnline(false);
    };

    useEffect(() => {
        setIsOnline(navigator.onLine);
    }, [isOnline]);
    useEffect(() => {
        // Add event listeners when the component is mounted
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Clean up the event listeners when the component is unmounted
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);
    useEffect(() => {
        const currentSection = location.pathname.substring(1);
        dispatch(
            setActiveIcon(
                currentSection === 'book' ? 'book-library' : currentSection
            )
        );
        dispatch(setActiveLink(location.pathname));
    }, [location.pathname, dispatch]);

    useEffect(() => {
        setLogo(product === 'Buildr' ? BuildrSideNavLogo : SmartTeachLogo);
    }, [product]);

    useEffect(() => {
        if (
            getStatusData &&
            getStatusData.data &&
            typeof getStatusData.data.sectionsCompleted === 'number' &&
            getStatusData.data.sectionsCompleted > 0
        ) {
            setIsPendingActionsModalOpen(false);
        } else {
            trackEvent('pending_actions_widget_viewed', {
                state: 'open',
                pending_items_count: pendingActionsCount,
                user_role: user?.rolename,
                school_id: schoolId,
                deal_id: dealId,
            })
        }
    }, [getStatusData]);

    const handleLinkClick = (
        e: any,
        key: string,
        label: string,
        url: string = ''
    ) => {
        e.stopPropagation();
        e.preventDefault();
        const path = `/${key.split('_').join('-')}`;
        const eventName = `side_menu_${key}_clicked`;
        //old event
        trackEvent(eventName, {
            nav_item: label,
            class_division: classDivision,
        });
        if (key === 'iframe_open') {
            setIframeDisplayUrl(url);
        } else if (key === 'settings') {
            dispatch(setPreferenceOpen(true));
            return;
        } else if (key === 'jasper' || key === 'assessments') {
            dispatch(setJasperIframeOpen(false));
        }
        if (isOnline) {
            if (process.env.REACT_APP_INSTALLATION_MODE !== 'webapp') {
                // Detect if the user is online or offline
                dispatch(setActiveLink(path));
                dispatch(setActiveIcon(path.substring(1)));
                if (path == '/book-library' || path == '/iframe-open') {
                    navigate(path);
                    setCollapsed(true);
                    return;
                }
                //online then open the external app
                // Trigger the Open Browser mutation
                const side_bar_base_url =
                    product === 'ActiveTeach'
                        ? process.env.REACT_APP_SIDE_BAR_AT_URL
                        : process.env.REACT_APP_SIDE_BAR_BUILDR_URL;
                const complete_url = side_bar_base_url + path;
                if (installationPackage === 'server') {
                    window.open(complete_url, '_blank');
                    return;
                }
                getOpenBrowser({ url: complete_url })
                    .unwrap() // This ensures we get the result of the mutation (or an error)
                    .then((response: any) => {
                        // Handle successful launch response
                        console.log('Open Browser API successful:', response);
                    })
                    .catch((error: any) => {
                        // Handle error
                        console.error('Open Browser API failed:', error);
                    });
            } else {
                dispatch(setActiveLink(path));
                dispatch(setActiveIcon(path.substring(1)));
                navigate(path);
                setCollapsed(true);
            }
        } else {
            e.preventDefault();
            if (path == '/book-library') {
                navigate(path);
                setCollapsed(true);
                return;
            }
            dispatch(setActiveIcon('book-library'));
            dispatch(setOfflineModalOpen(true));
            dispatch(setSideNavIconClickedName(label));
        }
        setCollapsed(true);
    };

    const handleMouseEnter = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setCollapsed(false);
    };

    const handleMouseLeave = () => {
        timerRef.current = setTimeout(() => {
            setCollapsed(true);
        }, 200);
    };

    const handleLogoClick = () => {
        navigate(`/book-library`);
    };
    const userRole = user?.rolename ? user?.rolename : 'Teacher';

    return (
        <aside
            className={`bg-white fixed flex flex-col justify-between font-medium left-0 transition-all duration-500 ease-in-out content-height overflow-y-auto scrollbar-0 ${
                collapsed
                    ? 'w-[4.375rem]'
                    : 'w-72 shadow-[0rem_0.375rem_0.25rem_0rem_#00000040]'
            }
            ${isFullScreenModalOpen || isTransparentModalOpen ? 'z-0' : 'z-50'}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <nav className="flex flex-col flex-grow">
                <div
                    className={`flex items-center my-4 ${
                        collapsed ? 'justify-center' : 'ml-4'
                    }`}
                >
                    <button
                        onClick={handleLogoClick}
                        data-testid="side-navigation-bar-logo-button"
                    >
                        <img className="h-10" src={logo} alt="Logo" />
                    </button>
                </div>

                {navList.sideNavItems.map((section: any, index: number) =>
                    section.visibleToRoles.includes(userRole) &&
                    section.visibleToProducts.includes(productType) &&
                    (section.availability === 'both' ||
                        (section.availability === 'online' &&
                            process.env.REACT_APP_INSTALLATION_MODE ===
                                'webapp') ||
                        (section.availability === 'offline' &&
                            process.env.REACT_APP_INSTALLATION_MODE ===
                                'desktop')) ? (
                        <div key={section.key}>
                            {index !== 0 && (
                                <hr
                                    className={`mx-3 ${collapsed ? 'my-3' : 'my-1'} `}
                                />
                            )}
                            {!collapsed && (
                                <p
                                    className={`text-black ml-4 opacity-40 text-[0.8125rem] transition-opacity duration-500 custom-text-limit ${
                                        collapsed
                                            ? 'opacity-0 w-0 ml-0'
                                            : 'opacity-100 ml-4'
                                    }`}
                                >
                                    {section?.subItems?.length > 0 &&
                                        section.displayName}
                                </p>
                            )}
                            {section.subItems?.map((item: any) =>
                                item.visibleToRoles.includes(userRole) &&
                                item.visibleToProducts.includes(productType) &&
                                (item.availability === 'both' ||
                                    (item.availability === 'online' &&
                                        process.env
                                            .REACT_APP_INSTALLATION_MODE ===
                                            'webapp') ||
                                    (item.availability === 'offline' &&
                                        process.env
                                            .REACT_APP_INSTALLATION_MODE ===
                                            'desktop')) ? (
                                    <div
                                        key={item.key}
                                        className={`flex items-center mx-2 my-1 h-10 rounded hover:cursor-pointer  ${
                                            activeIcon ===
                                                item.key.split('_').join('-') &&
                                            !collapsed
                                                ? 'bg-blue-200 bg-opacity-25'
                                                : 'hover:bg-[#BFD7FA] bg-opacity-25'
                                        }`}
                                        onClick={(e) => {
                                            handleLinkClick(
                                                e,
                                                item.key,
                                                item.displayName,
                                                item.params && item.params.url
                                                    ? item.params.url
                                                    : ''
                                            );
                                            setIsPendingActionsModalOpen(false);
                                            setIsWidgetLogoClicked(false);
                                        }}
                                    >
                                        <img
                                            alt="icon"
                                            className="h-8 px-2"
                                            src={
                                                activeIcon ===
                                                item.key.split('_').join('-')
                                                    ? icon_mapping[
                                                          item.activeIcon
                                                      ] || DefaultIcon
                                                    : icon_mapping[
                                                          item.inactiveIcon
                                                      ] || DefaultIcon
                                            }
                                        />
                                        <span
                                            className={`transition-opacity duration-500 custom-text-limit ${
                                                collapsed
                                                    ? 'opacity-0 w-0 ml-0'
                                                    : 'opacity-100 ml-4'
                                            }`}
                                        >
                                            {item.displayName}
                                        </span>
                                    </div>
                                ) : null
                            )}
                        </div>
                    ) : null
                )}
            </nav>

            {/* Preference Section  */}
            {isPreferenceOpen && (
                <UserPreferenceEditor
                    isOpen={isPreferenceOpen}
                    onClose={() => {
                        dispatch(setPreferenceOpen(false));
                        setCollapsed(true);
                    }}
                />
            )}
            {product === 'Buildr' &&
                process.env.REACT_APP_INSTALLATION_MODE === 'webapp' &&
                !isGetStatusLoading &&
                getStatusData &&
                getStatusData.data &&
                !widgetStatus &&
                isOnline && (
                    <div className="flex mb-8 px-4 items-center cursor-pointer">
                        <img
                            src={
                                pendingActionsCount !== 0
                                    ? WidgetLogo
                                    : WidgetLogoAllDone
                            }
                            alt="Widget Icon"
                            className={`${pendingActionsCount !== 0 ? 'w-[34px] rounded-xl ' : 'w-[28px]'} ${!collapsed ? 'mr-4' : ''}`}
                            onMouseEnter={() => {
                                setIsPendingActionsModalOpen(true);
                                trackEvent('pending_actions_widget_viewed', {
                                    state: 'open',
                                    pending_items_count: pendingActionsCount,
                                    user_role: user?.rolename,
                                    school_id: schoolId,
                                    deal_id: dealId,
                                })
                            }}
                            onClick={() => {
                                setIsPendingActionsModalOpen(true);
                                trackEvent('pending_actions_widget_viewed', {
                                    state: 'open',
                                    pending_items_count: pendingActionsCount,
                                    user_role: user?.rolename,
                                    school_id: schoolId,
                                    deal_id: dealId,
                                })
                                setIsWidgetLogoClicked(!isWidgetLogoClicked);
                            }}
                        />
                    </div>
                )}
            {process.env.REACT_APP_INSTALLATION_MODE === 'webapp' && (
                <div
                    className="flex mb-4 px-4 items-center cursor-pointer"
                    onClick={() => {
                        setIsModalOpen(true);
                    }}
                >
                    {isOnline && (
                        <img
                            onClick={() => {
                                setIsPendingActionsModalOpen(false);
                            }}
                            src={UserAvatar}
                            alt="User Icon"
                            className={`w-6 rounded-xl ${!collapsed ? 'ml-1 mr-4' : 'ml-1'}`}
                        />
                    )}
                    {!collapsed && user && user.name && isOnline && (
                        <div
                            className="flex justify-between items-center flex-grow"
                            onClick={() => {
                                setIsWidgetLogoClicked(false);
                                setIsPendingActionsModalOpen(false);
                            }}
                        >
                            <div className="flex flex-col">
                                <p className="text-black m-0 text-sm">
                                    {user.rolename}
                                </p>
                              
                                <span
                                className="font-light text-xs whitespace-nowrap overflow-hidden text-ellipsis max-w-[11rem] block"
                                title={schoolInfo.school.name} 
                                >
                                {schoolInfo.school.name}
                                </span>

                            </div>
                            <img src={LogOut} alt="Logout Icon" />
                        </div>
                    )}
                </div>
            )}
        </aside>
    );
};

export default SideNavigationBar;
